import React, { useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  getSourcePreview,
  isCmsGovSourceUrl,
  SourcePreviewResponse,
} from "@services/discover";

type CmsSourceLinkProps = {
  url: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
};

/**
 * Renders a source link. CMS.gov URLs open an in-app text preview via Rules
 * (bug 10937) instead of navigating the browser to cms.gov Akamai WAF.
 */
export function CmsSourceLink({
  url,
  children,
  style,
  className,
  title,
}: CmsSourceLinkProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SourcePreviewResponse | null>(null);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      if (!isCmsGovSourceUrl(url)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
      setLoading(true);
      setError(null);
      setPreview(null);
      try {
        const result = await getSourcePreview(url);
        setPreview(result);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load CMS.gov preview";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [url],
  );

  const cms = isCmsGovSourceUrl(url);

  return (
    <>
      <a
        href={url}
        target={cms ? undefined : "_blank"}
        rel={cms ? undefined : "noopener noreferrer"}
        onClick={handleClick}
        style={style}
        className={className}
        title={title}
      >
        {children ?? url}
      </a>
      {cms && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="md"
          BackdropProps={{
            sx: { backgroundColor: "rgba(0, 0, 0, 0.4)" },
          }}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography component="span" sx={{ fontWeight: 600, fontSize: 16 }}>
                {preview?.title || "CMS.gov source"}
              </Typography>
              <IconButton
                onClick={() => setOpen(false)}
                aria-label="Close source preview"
                size="small"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: 11,
                color: "#6b7280",
                mb: 1.5,
              }}
            >
              Opened via Unicomply (CMS.gov blocked some networks).
            </Typography>
            {preview?.url && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#9ca3af",
                  wordBreak: "break-all",
                  mb: 1.5,
                }}
              >
                {preview.url}
              </Typography>
            )}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} sx={{ color: "#0a829d" }} />
              </Box>
            )}
            {error && (
              <Typography sx={{ fontSize: 13, color: "#b91c1c" }}>{error}</Typography>
            )}
            {!loading && !error && preview && (
              <Typography
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "inherit",
                  fontSize: 13,
                  color: "#111827",
                  maxHeight: "60vh",
                  overflow: "auto",
                  m: 0,
                }}
              >
                {preview.text || "No text extracted from this page."}
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default CmsSourceLink;
